import { z } from 'zod';

/**
 * Конфигурация короткого кода ПОСТОЯННОЙ шеринговой ссылки (work-035,
 * `?startapp=b<s|c|d><code>` / `?startapp=a<code>`).
 *
 * ⚠️ НЕ путать с `codeConfig.ts` (`CODE_CONFIG`) — тот питает РОТИРУЕМЫЙ
 * `BoxCode` (ask-by-code, work-019/ADR 0005), живой в проде с 4-значными
 * numeric-кодами. Разные пространства кодов, разные сущности (ADR 0014):
 * `BoxCode` — отзываемый хэндл (ротация гасит канал), код ссылки здесь —
 * ПОСТОЯННЫЙ (ротация убила бы уже расшаренные ссылки). Правка `CODE_CONFIG`
 * ломает живые 4-значные коды — эта константа изолирована намеренно.
 *
 * Алфавит base62 `[0-9A-Za-z]` (без похожести не чистим — код не читается
 * человеком вслух, как BoxCode, а копипастится/шарится ссылкой целиком).
 * Длина 5 → 62^5 ≈ 916M вариантов (владелец: work-035 §2, ориентир 10M боксов).
 *
 * Browser-safe: без `node:crypto`. Генерация — `apps/api/src/lib/linkCodeGen.ts`
 * и `apps/bot/src/lib/linkCodeGen.ts` (продублирован — bot не может импортировать
 * api, `node:crypto` в shared нельзя, прецедент `generateBoxToken`).
 */
export const LINK_CODE_CONFIG = {
  length: 5,
  alphabet:
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
} as const;

/**
 * Экранирует строку для безопасного использования внутри символьного класса regex [...].
 * Зеркало `codeConfig.ts` — тот же приём, на случай будущей смены алфавита.
 */
function escapeForCharClass(str: string): string {
  return str.replace(/[\]\\^-]/g, '\\$&');
}

/**
 * Zod-схема валидации кода ссылки: строка ровно `LINK_CODE_CONFIG.length`
 * символов из `LINK_CODE_CONFIG.alphabet`.
 */
export const linkCodeSchema = z
  .string()
  .regex(
    new RegExp(
      `^[${escapeForCharClass(LINK_CODE_CONFIG.alphabet)}]{${LINK_CODE_CONFIG.length}}$`,
    ),
    `Link code must be exactly ${LINK_CODE_CONFIG.length} characters from base62 alphabet`,
  );

export type LinkCodeSchema = z.infer<typeof linkCodeSchema>;
